import React, { Fragment, useState, useMemo } from 'react';
import { always, complement, cond, isEmpty, pipe, prop, propEq, sortBy } from 'ramda';
import { useIntl } from 'gatsby-plugin-intl';
import { getTarget, getWithPrefix, findByType } from '../../utils';
import { ELEMENTS_TYPES } from '../../utils/constants';
import { CardLeft } from '../cardLeft';
import { Quote } from '../quote';
import {
  Container,
  Side,
  Main,
  MarkdownContent,
  PseudoLink,
  Link,
  Tooltip,
  ExpandButton,
  ExpandButtonWrapper,
} from './content.styles';
import { useCardLeft } from '../../hooks/useCardLeft';
import { CardContainer, CardToggleButton, CardToggleCloseIcon } from '../cardLeft/cardLeft.styles';
import { renderWhenTrue } from '../../utils/rendering';

const blockquote = ({ children }) => <Quote>{children}</Quote>;

const link = ({ href, title, children, ...props }) => {
  if (href === '/' && title) {
    return (
      <PseudoLink>
        <Fragment>{children}</Fragment>
        <Tooltip>{title}</Tooltip>
      </PseudoLink>
    );
  }

  return (
    <Link href={getWithPrefix(href)} target={getTarget(href)} {...props}>
      {children}
    </Link>
  );
};

const renderMain = ({ value }, key) => {
  const intl = useIntl();

  return (
    <MarkdownContent key={key} renderers={{ blockquote, link }}>
      {intl.formatMessage({ id: value })}
    </MarkdownContent>
  );
};

const anyElementHasValue = (elements) => elements.some(pipe(prop('value'), complement(isEmpty)));

const shouldSideBeRendered = (arr) => {
  if (isEmpty(arr)) return false;
  return arr.some(pipe(prop('elements'), anyElementHasValue));
};

export const Content = ({ elements, isExpandable = false }) => {
  const [isExpanded, setIsExpanded] = useState(() => !isExpandable);
  const orderedElements = useMemo(() => sortBy(prop('order'))(elements), [elements]);
  const sideElements = [];
  const mainElements = [];

  const card = useCardLeft();

  orderedElements.forEach(
    cond([
      [
        propEq('name', 'Card'),
        (element) => {
          sideElements.push(element);
        },
      ],
      [
        propEq('name', 'Content'),
        (element) => {
          mainElements.push(element);
        },
      ],
    ])
  );

  const renderCard = ({ elements }, key) => {
    const { value: markdown } = findByType(ELEMENTS_TYPES.MARKDOWN, elements);
    const { value: connection } = findByType(ELEMENTS_TYPES.CONNECTION, elements);

    if (isEmpty(markdown) && isEmpty(connection)) return null;

    return <CardLeft elements={elements} key={key} />;
  };

  const renderSide = ({ value, id }) => {
    return (
      <Fragment key={id}>
        {renderWhenTrue(
          always(
            <CardToggleButton isVisible={card.isVisible} onClick={card.toggleVisibility}>
              <CardToggleCloseIcon />
            </CardToggleButton>
          )
        )(shouldSideBeRendered(value))}
        <CardContainer isVisible={card.isVisible} ref={card.containerRef}>
          {value.map(renderCard)}
        </CardContainer>
      </Fragment>
    );
  };

  return (
    <Container isExpanded={isExpanded}>
      <Side>{sideElements.map(renderSide)}</Side>
      <Main>
        {mainElements.map(renderMain)}
        {renderWhenTrue(() => (
          <ExpandButtonWrapper>
            <ExpandButton onClick={() => setIsExpanded(true)}>Show more</ExpandButton>
          </ExpandButtonWrapper>
        ))(isExpandable && !isExpanded)}
      </Main>
    </Container>
  );
};
